import React, { useState } from 'react';
import { toNumber, flowRight as compose } from 'lodash';
import { Link, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';

import CtaButton from 'components/essentials/Cta/CtaButton';
import CtaLink from 'components/essentials/Cta/CtaLink';
import JBImage from 'components/essentials/JBImage';
import publicImgBaseURL from 'commons/publicImgBaseURL';
import LoginSidebar from 'components/consumer/LoginSidebar/LoginSidebar';
import LabeledSelectBox from 'components/consumer/LabeledSelectBox';
import ReviewStars from 'components/consumer/ReviewStars';
import useUserData from 'data-hooks/useUserData';
import ReviewsSearch from '../ReviewSearch';
import { sortOptions } from '../utils';

const CustomerReviewsHeader = ({
  averageScore,
  match,
  onSortChanged,
  product,
  refetch,
  searchState,
  subTitle,
  title,
  totalReviewsCount,
  type,
}) => {
  const { isLoggedIn } = useUserData();
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [selectedSortOption, setSelectedSortOption] = useState(sortOptions[0]);

  return (
    <>
      <LoginSidebar
        show={isSidebarOpen}
        onClose={() => {
          setSidebarOpen(false);
        }}
      />
      {type !== 'allProducts' && type !== 'productReviews' ? (
        <div className="max-w-[1400px] mt-8 mx-[3.333vw] mb-4 min-[1440px]:mt-[6.25rem] min-[1440px]:mx-auto min-[1440px]:mb-4">
          <h1 className="text-2xl text-gray mt-0 mx-0 mb-2 font-bold text-center">
            {title}
          </h1>
          <div className="text-lg text-gray mb-6 text-center">{subTitle}</div>
          <div className="flex flex-col min-[1440px]:px-5 min-[1440px]:py-0 max-[767px]:flex-col max-[767px]:items-center max-[767px]:justify-center max-[568px]:flex-col max-[568px]:items-center max-[568px]:justify-center">
            <div className="self-center mb-8">
              <div className="rating flex">
                <ReviewStars
                  rating={averageScore}
                  noSpacing
                  readOnly
                  size="sm"
                />
                <div className="text-sm pl-[.3rem]">
                  {toNumber(averageScore).toFixed(1)} / 5
                </div>
                <div className="text-sm pl-[.8rem]">
                  ({totalReviewsCount} Reviews)
                </div>
              </div>
            </div>
            <div className="flex justify-between items-center max-[767px]:flex-col max-[767px]:items-center max-[767px]:justify-center">
              <div className="w-2/5 max-[767px]:w-3/5 max-[767px]:mb-4 max-[568px]:w-full">
                {searchState ? (
                  <ReviewsSearch
                    inSearchMode={searchState.inSearchMode}
                    onSearchUpdate={searchState.onSearchUpdate}
                    onProductClicked={searchState.onProductClicked}
                    onProduct={searchState.onProduct}
                  />
                ) : null}
              </div>
              <div className='flex justify-end items-center w-1/2  flex-row max-[767px]:w-3/5 max-[767px]:flex-col max-[767px]:justify-center max-[568px]:w-full [&_.Select-value-label]:before:[content:"Sort_by:"]'>
                <div className="w-full max-[767px]:mb-4 border border-solid border-transparent mt-px">
                  <LabeledSelectBox
                    label="Sort by"
                    options={sortOptions}
                    value={selectedSortOption.value}
                    className="flex-1"
                    onChange={e => {
                      const selectedOption = sortOptions.find(
                        option => option.value === e.target.value
                      );
                      setSelectedSortOption(selectedOption);
                      onSortChanged(selectedOption, refetch);
                    }}
                    showBlankOption={false}
                    showSingleLineLabelOverlay
                  />
                </div>
                {isLoggedIn ? (
                  <div className="w-full pl-4 no-underline">
                    <CtaLink
                      to="/customer/my-reviews/"
                      styling="solid-button"
                      color="black"
                      block
                    >
                      Write A Review
                    </CtaLink>
                  </div>
                ) : (
                  <div className="w-full pl-4 no-underline">
                    <CtaButton
                      styling="solid-button"
                      color="black"
                      block
                      onClick={() => {
                        setSidebarOpen(true);
                      }}
                    >
                      Write A Review
                    </CtaButton>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {type === 'productReviews' && !!product ? (
        <div className="max-w-[1400px] mt-8 mx-[3.333vw] mb-4 min-[1440px]:mt-[6.25rem] min-[1440px]:mx-auto min-[1440px]:mb-4">
          <div className="text-2xl text-gray mt-0 mx-0 mb-2 font-bold text-center">
            Customers love the {product.name}
          </div>

          <div className="text-lg text-gray mb-6 text-center">{subTitle}</div>

          <div className="flex flex-col min-[1440px]:px-5 min-[1440px]:py-0 max-[767px]:flex-col max-[767px]:items-center max-[767px]:justify-center max-[568px]:flex-col max-[568px]:items-center max-[568px]:justify-center">
            <div className="flex w-full flex-row justify-between max-[767px]:flex-col max-[767px]:items-center max-[767px]:justify-center max-[568px]:flex-col max-[568px]:items-center max-[568px]:justify-center">
              <div className="w-1/2 flex justify-start max-[767px]:w-3/5 max-[767px]:mb-4 max-[568px]:w-full max-[568px]:flex max-[568px]:flex-col max-[568px]:text-center">
                <div>
                  {product.images && product.images.length && (
                    <div className="max-[568px]:w-full flex max-[765px]:w-full mr-4 w-40 items-center justify-center mb-4">
                      <JBImage
                        src={publicImgBaseURL(product.images[0].url)}
                        alt=""
                        loader
                        trim="color"
                        trimPad={20}
                        height={140}
                        className="[&_img]:object-contain"
                      />
                    </div>
                  )}
                </div>
                <div className="self-center mb-8">
                  {product.name}
                  <div className="rating flex">
                    <ReviewStars
                      rating={averageScore}
                      noSpacing
                      readOnly
                      size="sm"
                    />
                    <div className="text-sm pl-[.3rem]">
                      {toNumber(totalReviewsCount)} Reviews
                    </div>
                  </div>
                  <Link
                    className="text-base font-bold text-brand"
                    to={`/${match.params.category}/${match.params.slug}`}
                  >
                    Shop
                  </Link>
                </div>
              </div>

              <div className="w-1/2 mt-8 flex flex-row justify-end max-[767px]:w-3/5 max-[767px]:flex-col max-[767px]:justify-center max-[568px]:w-full">
                <div className="max-[767px]:mb-4 border border-solid border-transparent mt-px">
                  <LabeledSelectBox
                    label="Sort by"
                    options={sortOptions}
                    value={selectedSortOption.value}
                    className="flex-1"
                    onChange={e => {
                      const selectedOption = sortOptions.find(
                        option => option.value === e.target.value
                      );
                      setSelectedSortOption(selectedOption);
                      onSortChanged(selectedOption, refetch);
                    }}
                    showBlankOption={false}
                    showSingleLineLabelOverlay
                  />
                </div>

                {isLoggedIn ? (
                  <div className="no-underline [&_a]:md:ml-4 [&_button]:md:ml-4">
                    <CtaLink
                      to="/customer/my-reviews/"
                      styling="solid-button"
                      color="black"
                      block
                    >
                      Write A Review
                    </CtaLink>
                  </div>
                ) : (
                  <div className="no-underline [&_a]:md:ml-4 [&_button]:md:ml-4">
                    <CtaButton
                      styling="solid-button"
                      color="black"
                      block
                      onClick={() => {
                        setSidebarOpen(true);
                      }}
                    >
                      Write A Review
                    </CtaButton>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

CustomerReviewsHeader.propTypes = {
  averageScore: PropTypes.number,
  match: PropTypes.shape({
    params: PropTypes.shape({
      category: PropTypes.string,
      slug: PropTypes.string,
    }),
  }),
  onProduct: PropTypes.func,
  onProductClicked: PropTypes.func,
  onSearchUpdate: PropTypes.func,
  onSortChanged: PropTypes.func,
  product: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    images: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
      })
    ),
  }),
  refetch: PropTypes.func,
  searchState: PropTypes.shape({
    inSearchMode: PropTypes.bool,
    onProduct: PropTypes.func,
    onProductClicked: PropTypes.func,
    onSearchUpdate: PropTypes.func,
  }),
  selectedSortOption: PropTypes.shape({
    value: PropTypes.string,
  }),
  subTitle: PropTypes.string,
  title: PropTypes.string,
  totalReviewsCount: PropTypes.number,
  type: PropTypes.string,
};

export default compose(withRouter)(CustomerReviewsHeader);
